import { classifyError, isAuthError, isPermissionError, isValidationError, isRateLimitError, isNetworkError } from './error-handlers';

export interface RetryConfig {
  maxAttempts: number;
  baseDelay: number;
  maxDelay: number;
  backoffFactor: number;
  jitter: boolean;
  retryCondition?: (error: Error, attemptCount: number) => boolean;
}

export interface RetryResult<T> {
  success: boolean;
  data?: T;
  error?: Error;
  attempts: number;
  totalDelay: number;
}

// Default retry configurations for different scenarios
export const DEFAULT_RETRY_CONFIGS: Record<string, RetryConfig> = {
  // Network operations - aggressive retry
  network: {
    maxAttempts: 5,
    baseDelay: 1000,
    maxDelay: 30000,
    backoffFactor: 2,
    jitter: true,
    retryCondition: (error) => isNetworkError(error)
  },

  // API calls - moderate retry
  api: {
    maxAttempts: 3,
    baseDelay: 500,
    maxDelay: 5000,
    backoffFactor: 1.5,
    jitter: true,
    retryCondition: (error, attemptCount) => {
      const classified = classifyError(error);
      
      // Don't retry auth, permission, or validation errors
      if (isAuthError(error) || isPermissionError(error) || isValidationError(error)) {
        return false;
      }
      
      // Retry network and server errors
      return classified.retryable;
    }
  },

  // Critical operations - conservative retry
  critical: {
    maxAttempts: 2,
    baseDelay: 2000,
    maxDelay: 10000,
    backoffFactor: 2,
    jitter: false,
    retryCondition: (error) => isNetworkError(error) || isRateLimitError(error)
  },

  // Rate limit specific - longer delays
  rateLimit: {
    maxAttempts: 4,
    baseDelay: 5000,
    maxDelay: 60000,
    backoffFactor: 2,
    jitter: true,
    retryCondition: (error) => isRateLimitError(error)
  },

  // No retry for immediate failures
  none: {
    maxAttempts: 1,
    baseDelay: 0,
    maxDelay: 0,
    backoffFactor: 1,
    jitter: false,
    retryCondition: () => false
  }
};

// Calculate delay with exponential backoff and optional jitter
function calculateDelay(attempt: number, config: RetryConfig): number {
  const exponentialDelay = config.baseDelay * Math.pow(config.backoffFactor, attempt);
  let delay = Math.min(exponentialDelay, config.maxDelay);
  
  // Add jitter to prevent thundering herd
  if (config.jitter) {
    delay = delay * (0.5 + Math.random() * 0.5);
  }
  
  return Math.floor(delay);
}

// Sleep function for delays
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// Generic retry function with exponential backoff
export async function retryWithBackoff<T>(
  operation: () => Promise<T>,
  config: RetryConfig = DEFAULT_RETRY_CONFIGS.api
): Promise<RetryResult<T>> {
  let lastError: Error | undefined;
  let totalDelay = 0;
  
  for (let attempt = 0; attempt < config.maxAttempts; attempt++) {
    try {
      const data = await operation();
      return {
        success: true,
        data,
        attempts: attempt + 1,
        totalDelay
      };
    } catch (error) {
      lastError = error as Error;
      
      // Check if we should retry this error
      if (config.retryCondition && !config.retryCondition(lastError, attempt + 1)) {
        break;
      }
      
      // Don't delay after the last attempt
      if (attempt < config.maxAttempts - 1) {
        const delay = calculateDelay(attempt, config);
        totalDelay += delay;
        await sleep(delay);
      }
    }
  }
  
  return {
    success: false,
    error: lastError,
    attempts: config.maxAttempts,
    totalDelay
  };
}

// Fetch wrapper with retry logic
export async function retryFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
  retryConfig: RetryConfig = DEFAULT_RETRY_CONFIGS.network
): Promise<Response> {
  const result = await retryWithBackoff(
    () => fetch(input, init),
    retryConfig
  );
  
  if (result.success && result.data) {
    return result.data;
  }
  
  throw result.error || new Error('Fetch failed after all retry attempts');
}

// Supabase query wrapper with retry logic
export async function retrySupabaseQuery<T>(
  queryFn: () => Promise<T>,
  config: RetryConfig = DEFAULT_RETRY_CONFIGS.api
): Promise<T> {
  const result = await retryWithBackoff(queryFn, config);
  
  if (result.success && result.data !== undefined) {
    return result.data;
  }
  
  throw result.error || new Error('Supabase query failed after all retry attempts');
}

// Circuit breaker for preventing repeated failures
export class CircuitBreaker {
  private failureCount = 0;
  private lastFailureTime?: Date;
  private state: 'closed' | 'open' | 'half-open' = 'closed';
  
  constructor(
    private failureThreshold: number = 5,
    private resetTimeout: number = 60000 // 1 minute
  ) {}
  
  async execute<T>(operation: () => Promise<T>): Promise<T> {
    if (this.state === 'open') {
      if (this.shouldAttemptReset()) {
        this.state = 'half-open';
      } else {
        throw new Error('Circuit breaker is open');
      }
    }
    
    try {
      const result = await operation();
      this.onSuccess();
      return result;
    } catch (error) {
      this.onFailure();
      throw error;
    }
  }
  
  private onSuccess(): void {
    this.failureCount = 0;
    this.state = 'closed';
  }
  
  private onFailure(): void {
    this.failureCount++;
    this.lastFailureTime = new Date();
    
    if (this.failureCount >= this.failureThreshold) {
      this.state = 'open';
    }
  }
  
  private shouldAttemptReset(): boolean {
    if (!this.lastFailureTime) return false;
    
    const timeSinceLastFailure = Date.now() - this.lastFailureTime.getTime();
    return timeSinceLastFailure >= this.resetTimeout;
  }
  
  getState() {
    return {
      state: this.state,
      failureCount: this.failureCount,
      lastFailureTime: this.lastFailureTime
    };
  }
  
  reset(): void {
    this.failureCount = 0;
    this.lastFailureTime = undefined;
    this.state = 'closed';
  }
}

// Global circuit breakers for common operations
export const circuitBreakers = {
  api: new CircuitBreaker(5, 60000),
  auth: new CircuitBreaker(3, 120000),
  database: new CircuitBreaker(5, 30000)
};

// Retry with circuit breaker
export async function retryWithCircuitBreaker<T>(
  operation: () => Promise<T>,
  circuitBreaker: CircuitBreaker,
  retryConfig: RetryConfig = DEFAULT_RETRY_CONFIGS.api
): Promise<T> {
  return circuitBreaker.execute(async () => {
    const result = await retryWithBackoff(operation, retryConfig);
    
    if (result.success && result.data !== undefined) {
      return result.data;
    }
    
    throw result.error || new Error('Operation failed after all retry attempts');
  });
}

// React Query retry function
export function getReactQueryRetryFn(
  config: RetryConfig = DEFAULT_RETRY_CONFIGS.api
) {
  return (failureCount: number, error: Error) => {
    if (failureCount >= config.maxAttempts) {
      return false;
    }
    
    return config.retryCondition ? config.retryCondition(error, failureCount) : true;
  };
}

// React Query retry delay function
export function getReactQueryRetryDelay(
  config: RetryConfig = DEFAULT_RETRY_CONFIGS.api
) {
  return (attemptIndex: number) => calculateDelay(attemptIndex, config);
}

// Specialized retry strategies
export const retryStrategies = {
  // For authentication requests
  auth: (operation: () => Promise<any>) =>
    retryWithBackoff(operation, DEFAULT_RETRY_CONFIGS.none),
  
  // For network requests
  network: (operation: () => Promise<any>) =>
    retryWithBackoff(operation, DEFAULT_RETRY_CONFIGS.network),
  
  // For API calls
  api: (operation: () => Promise<any>) =>
    retryWithBackoff(operation, DEFAULT_RETRY_CONFIGS.api),
  
  // For critical operations
  critical: (operation: () => Promise<any>) =>
    retryWithBackoff(operation, DEFAULT_RETRY_CONFIGS.critical),
  
  // For rate-limited operations
  rateLimit: (operation: () => Promise<any>) =>
    retryWithBackoff(operation, DEFAULT_RETRY_CONFIGS.rateLimit),

  // With circuit breaker protection
  withCircuitBreaker: <T>(
    operation: () => Promise<T>,
    breakerName: keyof typeof circuitBreakers = 'api'
  ) => 
    retryWithCircuitBreaker(operation, circuitBreakers[breakerName]),
};

// Utility to create a retry-enabled fetch function
export function createRetryFetch(config?: Partial<RetryConfig>) {
  const finalConfig = { ...DEFAULT_RETRY_CONFIGS.network, ...config };
  
  return (input: RequestInfo | URL, init?: RequestInit) =>
    retryFetch(input, init, finalConfig);
}

// Utility to create retry-enabled query function
export function createRetryQuery<T>(
  queryFn: () => Promise<T>,
  config?: Partial<RetryConfig>
) {
  const finalConfig = { ...DEFAULT_RETRY_CONFIGS.api, ...config };
  
  return () => retrySupabaseQuery(queryFn, finalConfig);
}